import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Reveal, { Eyebrow } from "@/components/Reveal";
import QuoteSla from "@/components/QuoteSla";
import BrushDivider from "@/components/BrushDivider";

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      if (window.location.hash) {
        setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" }), 300);
      }
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="services-page" className="pt-24 sm:pt-28">
      <Seo title="Services" description="Graphic design, branding, image editing, video editing and creator content — one-off projects or ongoing subscriptions." path="/services" />
      <header className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <Eyebrow>SERVICES</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-balance">
          Everything your brand <span className="text-arsenal">needs to exist.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
          Logos. Graphics. Video. Branding. Whatever you're building, we'll help make it look the part.
          Every service below is a starting point — if you don't see exactly what you need, ask anyway.
        </p>
        <QuoteSla className="mt-6" />
      </header>

      <BrushDivider />

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {services.map((s, i) => (
          <Reveal key={s.id}>
            <section id={s.slug} data-testid={`service-${s.slug}`} className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-16 py-16 border-b border-white/5 scroll-mt-28">
              <div>
                <span className="font-mono text-xs tracking-[0.3em] text-arsenal">0{i + 1}</span>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">{s.title}</h2>
                <p className="mt-3 text-slate-300">{s.tagline}</p>
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">{s.description}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <Link
                    to={`/start-a-project?type=${s.slug}`}
                    data-testid={`service-cta-${s.slug}`}
                    className="clip-btn inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors"
                  >
                    START A PROJECT <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                  {s.quote_only && (
                    <span className="font-mono text-[11px] tracking-[0.2em] text-slate-500 border border-white/15 px-3 py-2">REQUEST A QUOTE</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap content-start gap-2">
                {s.items.map((item) => (
                  <span key={item} className="border border-white/10 bg-ink-surface hover:border-arsenal/60 hover:text-white px-3.5 py-2 text-sm text-slate-400 transition-colors">
                    {item}
                  </span>
                ))}
              </div>
            </section>
          </Reveal>
        ))}

        {/* NEED SOMETHING ELSE */}
        <Reveal>
          <section className="relative my-20 overflow-hidden border border-arsenal/30 bg-gradient-to-br from-arsenal/15 via-transparent to-transparent p-10 sm:p-16 text-center" data-testid="services-custom-cta">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">NEED SOMETHING ELSE?</h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-300 leading-relaxed">
              If you can describe it, there is a good chance we can create it.
            </p>
            <Link
              to="/start-a-project?type=custom"
              data-testid="services-custom-btn"
              className="clip-btn mt-8 inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-sm px-9 py-4 transition-colors shadow-[0_0_35px_rgba(229,9,20,0.35)]"
            >
              TELL US ABOUT YOUR PROJECT <ArrowUpRight className="h-4 w-4" />
            </Link>
          </section>
        </Reveal>
      </div>
    </div>
  );
}
